import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Award, Copy, Linkedin, Search, Trophy, Users } from "lucide-react";
import { toast } from "sonner";

/* Three intro-message templates. Each function receives {name, percent, score, total, certId}
   and returns a copyable message. */
const TEMPLATES = [
  {
    id: "congrats",
    label: "Congratulations (warm)",
    tone: "warm",
    build: ({ name, percent, certId }) =>
      `Hi ${name.split(" ")[0]} 👋 — Just saw you crushed the AFTR practice at ${percent}%. Congratulations! ` +
      `That's exactly the kind of readiness clients notice at intake. Your certificate ID (${certId}) is public — ` +
      `share it wherever you list credentials. Proud to have you in the DyNamic Tax cohort. — Di'Meka`,
  },
  {
    id: "referral",
    label: "Referral ask (business)",
    tone: "business",
    build: ({ name, percent }) =>
      `Hi ${name.split(" ")[0]}, congrats again on scoring ${percent}% on the AFTR practice — you're the kind of preparer ` +
      `I actively refer clients to. If you know one or two colleagues who could benefit from the AFSP Prep Bundle ` +
      `before the season lands, could you send them my way? I'll credit any new preparers you send with a bundle discount. ` +
      `Grateful for your work. — Di'Meka`,
  },
  {
    id: "testimonial",
    label: "Testimonial request (marketing)",
    tone: "marketing",
    build: ({ name, percent }) =>
      `Hi ${name.split(" ")[0]} — because you passed the AFTR simulation at ${percent}%, I'd love a quick 2-3 sentence ` +
      `testimonial about the Circular 230 study guide and the practice exam. I'd use it on the DyNamic Tax site to help ` +
      `other seasoned preparers decide. In exchange, I'll spotlight you (with your LinkedIn) as a featured graduate this ` +
      `season. Reply with a line or two whenever you have a minute — thank you! — Di'Meka`,
  },
];

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch { return iso; }
}

export default function AdminAfspCohort() {
  const [cohort, setCohort] = useState([]);
  const [q, setQ] = useState("");
  const [openMember, setOpenMember] = useState(null);
  const [activeTpl, setActiveTpl] = useState("congrats");

  useEffect(() => {
    api.get("/afsp/admin/cohort")
      .then((r) => setCohort(r.data.cohort || []))
      .catch(() => toast.error("Could not load cohort"));
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return cohort;
    const needle = q.trim().toLowerCase();
    return cohort.filter((c) =>
      (c.user_name || "").toLowerCase().includes(needle) ||
      (c.user_email || "").toLowerCase().includes(needle) ||
      (c.cert_id || "").toLowerCase().includes(needle),
    );
  }, [cohort, q]);

  const stats = useMemo(() => {
    const total = cohort.length;
    const avg = total ? Math.round(cohort.reduce((s, c) => s + (c.percent || 0), 0) / total) : 0;
    const withSim = cohort.filter((c) => c.best_exam_sim).length;
    return { total, avg, withSim };
  }, [cohort]);

  const copyTpl = async (tpl, member) => {
    try {
      const text = tpl.build({
        name: member.user_name || "there",
        percent: member.percent,
        score: member.score,
        total: member.total,
        certId: member.cert_id,
      });
      await navigator.clipboard.writeText(text);
      toast.success(`Copied "${tpl.label}"`);
    } catch {
      toast.error("Copy failed");
    }
  };

  const openLinkedIn = (member) => {
    // If no linkedin URL on file, open the LinkedIn search prefilled with their name.
    const url = member.linkedin_url
      ? member.linkedin_url
      : `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(member.user_name || "")}`;
    window.open(url, "_blank", "noopener");
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 lg:p-12">
        <div className="label-caps mb-3 flex items-center gap-2"><Users size={14} /> AFSP Cohort · Instructor View</div>
        <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-8">Your certificate earners.</h1>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <Card className="p-5" data-testid="cohort-stat-total">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total earners</div>
            <div className="font-serif text-4xl mt-1">{stats.total}</div>
          </Card>
          <Card className="p-5" data-testid="cohort-stat-avg">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Average score</div>
            <div className="font-serif text-4xl mt-1 text-primary">{stats.avg}%</div>
          </Card>
          <Card className="p-5" data-testid="cohort-stat-sim">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Passed full sim</div>
            <div className="font-serif text-4xl mt-1 text-accent">{stats.withSim}</div>
          </Card>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or certificate ID"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9"
            data-testid="cohort-search"
          />
        </div>

        {/* List */}
        {filtered.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground" data-testid="cohort-empty">
            <Trophy size={26} className="mx-auto mb-3 opacity-50" />
            {cohort.length === 0
              ? "No AFSP certificates issued yet. The moment a student passes the 70% threshold, they'll appear here."
              : `No results for "${q}".`}
          </Card>
        ) : (
          <div className="space-y-3" data-testid="cohort-list">
            {filtered.map((m) => (
              <Card key={m.id || m.cert_id} className="p-5" data-testid={`cohort-row-${m.cert_id}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className="h-14 w-14 rounded-full bg-primary/10 text-primary grid place-items-center shrink-0">
                    <Award size={22} />
                  </div>
                  <div className="flex-1 min-w-[220px]">
                    <div className="flex items-baseline gap-3 flex-wrap">
                      <h3 className="font-serif text-2xl">{m.user_name}</h3>
                      <span className="text-[10px] uppercase tracking-widest text-primary font-medium">
                        Cert · {m.cert_id}
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-0.5">{m.user_email}</div>
                    <div className="flex flex-wrap gap-3 mt-3 text-sm">
                      <span><strong className="text-emerald-600">{m.percent}%</strong> · {m.score}/{m.total}</span>
                      <span className="text-muted-foreground">Issued {fmtDate(m.issued_at)}</span>
                      {m.best_exam_sim && (
                        <span className="px-2 py-0.5 rounded-full bg-accent/10 text-accent text-[10px] font-medium uppercase tracking-widest">
                          Full sim · {m.best_exam_sim.percent}%
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLinkedIn(m)}
                      data-testid={`cohort-linkedin-${m.cert_id}`}
                    >
                      <Linkedin size={13} className="mr-1.5" /> Find on LinkedIn
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => { setOpenMember(m); setActiveTpl("congrats"); }}
                      data-testid={`cohort-outreach-${m.cert_id}`}
                    >
                      <Copy size={13} className="mr-1.5" /> Outreach templates
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Outreach template modal */}
      {openMember && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={() => setOpenMember(null)}>
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="cohort-outreach-modal">
            <div className="label-caps mb-2 text-primary">Outreach templates · {openMember.user_name}</div>
            <div className="flex flex-wrap gap-2 mb-4">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  onClick={() => setActiveTpl(t.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${activeTpl === t.id ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}
                  data-testid={`tpl-tab-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {(() => {
              const tpl = TEMPLATES.find((t) => t.id === activeTpl);
              const preview = tpl.build({
                name: openMember.user_name || "there",
                percent: openMember.percent,
                score: openMember.score,
                total: openMember.total,
                certId: openMember.cert_id,
              });
              return (
                <>
                  <div
                    className="p-4 rounded-md bg-secondary/60 text-sm leading-relaxed whitespace-pre-wrap mb-4 border border-border"
                    data-testid={`tpl-preview-${tpl.id}`}
                  >
                    {preview}
                  </div>
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="ghost" onClick={() => setOpenMember(null)}>Close</Button>
                    <Button variant="outline" onClick={() => openLinkedIn(openMember)}>
                      <Linkedin size={13} className="mr-1.5" /> Open LinkedIn
                    </Button>
                    <Button onClick={() => copyTpl(tpl, openMember)} data-testid="tpl-copy-btn">
                      <Copy size={13} className="mr-1.5" /> Copy message
                    </Button>
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </Layout>
  );
}
